import { tv } from "../../variants/tv.js";
const inputButton = tv({
    slots: {
        root: 'group relative flex flex-col gap-1 antialiased transition duration-200',
        labelSlot: 'relative text-body1 text-primary-10',
        sublabelSlot: 'relative truncate text-body1',
        buttonText: [
            'relative flex flex-1 flex-col justify-center truncate text-left transition duration-200'
        ],
        buttonWrapper: [
            'justify-items-between relative flex cursor-pointer items-center gap-3 rounded-xl border bg-primary-1 px-4 py-2 transition-colors',
            'border-primary-6 focus-within:border-primary-8 focus-within:shadow-input-focus',
            'group-data-[focus-within=true]:border-primary-8 group-data-[focus-within=true]:shadow-input-focus group-data-[focus-within=true]:hover:border-primary-8 group-data-[focus-within=true]:hover:shadow-input-focus',
            'focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:text-neutral-7',
            'aria-[invalid=true]:border-error-8'
        ],
        textSlot: 'mt-2 ml-4 flex justify-between text-body3',
        helperTextSlot: 'text-primary-13',
        errorTextSlot: 'text-error-8',
        iconLeft: 'text-primary-8 focus-visible:outline-none',
        iconRight: 'text-primary-6 focus-visible:outline-none'
    },
    variants: {
        hasPlaceholder: {
            true: {
                labelSlot: 'text-body3 text-primary-10',
                sublabelSlot: 'text-primary-10'
            }
        },
        hasValue: {
            true: {
                labelSlot: 'text-body3 text-primary-10',
                sublabelSlot: 'text-primary-13'
            }
        },
        size: {
            sm: {
                buttonWrapper: 'h-[32px] rounded-xl'
            },
            lg: {
                buttonWrapper: 'h-[48px]'
            },
            xl: {
                buttonWrapper: 'h-[56px]'
            }
        },
        emphasis: {
            regular: {},
            medium: {},
            bold: {
                buttonWrapper: [
                    'bg-primary-1/20',
                    'border-primary-1/40 focus-within:border-primary-1 hover:border-primary-1 focus-within:hover:border-primary-1',
                    'group-data-[focus-within=true]:border-primary-1 group-data-[focus-within=true]:hover:border-primary-1'
                ],
                labelSlot: 'text-primary-1',
                sublabelSlot: 'text-primary-1',
                iconLeft: 'text-primary-1',
                iconRight: 'text-primary-1'
            }
        },
        isDisabled: {
            true: {
                buttonWrapper: [
                    'cursor-not-allowed bg-primary-2 disabled:text-primary-6',
                    'hover:border-primary-6 hover:shadow-none',
                    'focus-within:border-primary-6 focus-within:hover:border-primary-6',
                    'group-data-[focus-within=true]:border-primary-6 group-data-[focus-within=true]:hover:border-primary-6'
                ],
                labelSlot: 'text-primary-6',
                sublabelSlot: 'text-primary-6',
                iconLeft: 'text-primary-6',
                iconRight: 'text-primary-6'
            },
            false: {
                buttonWrapper: [
                    'hover:border-primary-10 group-data-focused:hover:border-primary-8',
                    'transition hover:shadow-input-hover'
                ]
            }
        },
        fullWidth: {
            true: {
                buttonWrapper: 'w-full'
            }
        },
        error: {
            true: {
                buttonWrapper: [
                    'border-error-7',
                    'transition focus-within:shadow-input-error hover:shadow-input-error',
                    'group-data-[focus-within=true]:shadow-input-error'
                ]
            },
            false: {
                buttonWrapper: [
                    'focus-within:ring-2 focus-within:ring-primary-8/20',
                    'group-data-[focus-within=true]:ring-2 group-data-[focus-within=true]:ring-primary-8/20'
                ]
            }
        }
    },
    compoundVariants: [
        {
            hasPlaceholder: false,
            hasValue: false,
            class: {
                labelSlot: 'text-body1 text-primary-10'
            }
        },
        {
            hasPlaceholder: false,
            hasValue: false,
            emphasis: 'bold',
            class: {
                labelSlot: 'text-primary-1'
            }
        },
        {
            hasPlaceholder: true,
            emphasis: 'bold',
            class: {
                labelSlot: 'text-primary-1',
                sublabelSlot: 'text-primary-5',
                helperTextSlot: 'text-primary-1'
            }
        },
        {
            hasValue: true,
            emphasis: 'bold',
            class: {
                labelSlot: 'text-primary-1',
                sublabelSlot: 'text-primary-1'
            }
        },
        {
            isDisabled: true,
            emphasis: 'medium',
            class: {
                buttonWrapper: 'disabled:border-primary-5 disabled:bg-primary-2 disabled:hover:border-primary-5',
                iconLeft: 'text-primary-6',
                iconRight: 'text-primary-6',
                labelSlot: 'text-primary-6',
                sublabelSlot: 'text-primary-6'
            }
        },
        {
            isDisabled: true,
            emphasis: 'bold',
            class: {
                buttonWrapper: [
                    'bg-primary-1/30',
                    'border-primary-1/10',
                    'cursor-not-allowed',
                    'hover:border-primary-1/40',
                    'hover:shadow-none',
                    'focus-within:shadow-none',
                    'group-data-[focus-within=true]:shadow-none'
                ],
                labelSlot: 'text-primary-6 opacity-50',
                sublabelSlot: 'text-primary-6/50',
                iconLeft: 'text-primary-6/40',
                iconRight: 'text-primary-6/40'
            }
        },
        {
            error: true,
            emphasis: 'bold',
            class: {
                buttonWrapper: [
                    'bg-primary-1/20',
                    'transition hover:border-error-4',
                    'focus-within:border-error-4',
                    'transition focus-within:hover:border-error-7',
                    'group-data-[focus-within=true]:border-error-4 group-data-[focus-within=true]:hover:border-error-7'
                ],
                labelSlot: 'text-primary-1',
                sublabelSlot: 'text-primary-1',
                errorTextSlot: 'text-error-7'
            }
        },
        {
            error: true,
            emphasis: 'medium',
            class: {
                buttonWrapper: [
                    'transition hover:border-error-8',
                    'focus-within:border-error-8',
                    'transition focus-within:hover:border-error-8',
                    'transition focus-within:shadow-input-error hover:shadow-input-error',
                    'group-data-[focus-within=true]:border-error-8 group-data-[focus-within=true]:shadow-input-error group-data-[focus-within=true]:hover:border-error-8'
                ]
            }
        },
        {
            error: true,
            emphasis: 'regular',
            class: {
                buttonWrapper: [
                    'bg-primary-1/20',
                    'transition hover:border-error-8',
                    'focus-within:border-error-8',
                    'transition focus-within:hover:border-error-8',
                    'transition focus-within:shadow-input-error hover:shadow-input-error',
                    'group-data-[focus-within=true]:border-error-8 group-data-[focus-within=true]:shadow-input-error group-data-[focus-within=true]:hover:border-error-8'
                ]
            }
        },
        {
            hasPlaceholder: true,
            size: 'sm',
            class: {
                labelSlot: 'top-[4px]',
                sublabelSlot: '-top-[2px]'
            }
        },
        {
            hasValue: true,
            size: 'sm',
            class: {
                labelSlot: 'top-[4px]',
                sublabelSlot: '-top-[2px]'
            }
        }
    ],
    defaultVariants: {
        emphasis: 'regular',
        size: 'lg'
    }
});
const inputButtonVariants = inputButton;
const inputButtonSizes = Object.keys(inputButtonVariants.variants.size);
const inputButtonEmphases = Object.keys(inputButtonVariants.variants.emphasis);
export { inputButton, inputButtonEmphases, inputButtonSizes, inputButtonVariants };
